import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { WebUxTitle } from '../WebUxContext';
import { Button, Table, Tag } from 'antd';
import { Permission, UserGroup } from '../../dto';
import { EditOutlined } from '@ant-design/icons';
import { useEventListener } from '../../common/EventContext';
import { useBusinessContext } from '../../context/BusinessContext';
import UserGroupCreateModal, {
  useUserGroupCreateModal,
} from './UserGroupCreateModal';
import { UserRole } from '../../dto';
import { useConnection, useUser } from '../../connection/connection';

type Props = {};

export const UserGroupPage: FC<Props> = (props) => {
  const connection = useConnection();
  const business = useBusinessContext();
  const createModal = useUserGroupCreateModal();
  const userInfo = useUser();
  const [loading, setLoading] = useState<boolean>(false);
  const [groups, setGroups] = useState<UserGroup[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);

  useEffect(() => {
    setLoading(true);
    connection
      .get(`user-group/${business.businessId}`)
      .then((res) => {
        setGroups(res?.data ?? []);
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [business.businessId, connection]);

  useEventListener(
    'GROUP',
    useCallback((evt) => {
      if (evt.action === 'CREATE') {
        setGroups((prevState) => [...prevState, evt.data]);
      } else if (evt.action === 'UPDATE') {
        setGroups((prevState) =>
          prevState.map((s) => (s.groupId === evt.data.groupId ? evt.data : s)),
        );
      } else {
        setGroups((prevState) =>
          prevState.filter((s) => s.groupId !== evt.data.groupId),
        );
      }
    }, []),
  );

  const columns: any = useMemo(
    () => [
      {
        title: 'Name',
        dataIndex: 'name',
        key: 'name',
      },
      {
        title: 'Permissions',
        dataIndex: 'permissions',
        key: 'permissions',
        render: (permissions: string[]) => (
          <div className="flex flex-wrap gap-1">
            {permissions && permissions.length > 0 ? (
              permissions.map((permission, index) => {
                const foundEntry = Object.entries(Permission).find(
                  ([_, v]) => v === permission,
                );

                return (
                  <Tag key={index} color="blue">
                    {foundEntry?.[0] || 'Unknown'}
                  </Tag>
                );
              })
            ) : (
              <span>No permissions</span>
            )}
          </div>
        ),
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        render: (status: string) => {
          if (!status) return <div />;
          const color = status?.toLowerCase() === 'active' ? 'green' : 'red';
          return (
            <Tag color={color} key={status}>
              {status?.toUpperCase()}
            </Tag>
          );
        },
      },
      {
        title: 'Actions',
        key: 'action',
        hidden: !userInfo?.permissions?.includes(Permission.EDIT_USER_GROUP),
        render: (text: string, record: UserGroup) => (
          <Button
            type={'link'}
            onClick={() => createModal?.current?.edit(record)}
          >
            <EditOutlined />
          </Button>
        ),
      },
    ],
    [createModal, userInfo?.permissions],
  );

  return (
    <div className="flex flex-col gap-2">
      <WebUxTitle>User Groups</WebUxTitle>
      <div className="flex justify-end">
        {userInfo?.permissions?.includes(Permission.CREATE_USER_GROUP) && (
          <Button
            type="primary"
            onClick={() => {
              createModal?.current?.create();
            }}
          >
            CREATE USER GROUP
          </Button>
        )}
      </div>
      <Table
        size="small"
        loading={loading}
        //pagination={false}
        columns={columns}
        dataSource={groups}
        pagination={{
          total: groups?.length ?? 0,
          current: currentPage,
          pageSize,
          showSizeChanger: true,
        }}
        onChange={(pagination: any) => {
          setCurrentPage(pagination.page);
          setPageSize(pagination.pageSize);
        }}
      />
      <UserGroupCreateModal ref={createModal} />
    </div>
  );
};
